// Scenario specification + mode/game vocabulary. ENGINE_DESIGN.md §1 (schema/scenario.ts).
import { z } from 'zod';
import { PositionSchema } from './position';
import { ArchetypeIdSchema } from './archetype';
import { TableTypeSchema, LearningLayerSchema, TrainingContextSchema } from './read';
import { IcmPressureSchema } from './buckets';
/** The six v1 modes (ARCHITECTURE.md §6 sessions.mode; §9 mode table). */
export const ModeIdSchema = z.enum([
    'guided',
    'no_tips',
    'range_reading',
    'what_changed',
    'casino_lab',
    'icm_lab',
]);
export const GameTypeSchema = z.enum(['cash', 'tournament']);
/** Tournament phase (ENGINE_DESIGN.md §1). Aliased as TournamentStage for record columns. */
export const TournamentPhaseSchema = z.enum([
    'early',
    'middle',
    'bubble',
    'itm',
    'finalTable',
    'headsUp',
]);
export const TournamentStageSchema = TournamentPhaseSchema;
/** The depth a hand OPENS at when seats aren't given individually (hand/init.ts startingStacks
 *  overrides it per seat). An arbitrary positive number - UNCAPPED, no preset list (TB0 §4.3).
 *  The old {10,20,40,80,150} union was a UI-preset artifact the engine already routed around:
 *  hand/pluribus.ts replays 100bb fixtures and used to cast `as unknown as ScenarioSpec` to skip
 *  validation, noting the enum was "scoped to the UI scenario generator's presets, not to what
 *  advance()/legalActions() actually need". It could never survive carried stacks either - win a
 *  pot and you sit on $437, which is in no list of five. Every consumer does arithmetic on this
 *  (hand/init.ts, hand/pluribus.ts); nothing switches on it. Grading is unaffected: the facts layer
 *  buckets the REAL effective stack through stackDepthBucketOf, a range mapper, not an enum lookup. */
export const StackDepthBBSchema = z.number().positive();
/** Blind structure for a ScenarioSpec (extracted at D2 so stakes presets reuse the exact shape). */
export const StakesSchema = z.object({
    sb: z.number().nonnegative(),
    bb: z.number().nonnegative(),
    ante: z.number().nonnegative().optional(),
    currency: z.enum(['USD', 'chips']),
});
/** Where a stakes preset sits on the Local Casino Lab ladder (North Star §5): $1/$3 is the primary
 *  game, $3/$5 the graduate game, $5/$10/$20 the watch game. */
export const StakesTierSchema = z.enum(['primary', 'graduate', 'watch']);
/** A named stakes default for casino_lab (data/stakes/stakes.v1.json). `stakes` drops directly
 *  into ScenarioSpec.stakes. WA capped/spread-limit structure is DEFERRED post-v1 - presets carry
 *  NO bet-cap or max-raise numbers; structural oddities live in `note` as prose only. */
export const StakesPresetSchema = z.object({
    id: z.string(),
    /** display label, e.g. '$1/$3 NL'. */
    label: z.string(),
    gameType: GameTypeSchema,
    stakes: StakesSchema,
    tier: StakesTierSchema,
    note: z.string().optional(),
});
/** stakes.v1.json as a versioned unit; defaultPresetId names the preset casino_lab boots with. */
export const StakesPresetTableSchema = z.object({
    version: z.number().int(),
    defaultPresetId: z.string(),
    presets: z.array(StakesPresetSchema),
});
export const ScenarioSpecSchema = z.object({
    gameType: GameTypeSchema,
    /** The CHART-FAMILY selector, normalized `<=6 ? 6 : 9` before it reaches familyId(). The real seat
     *  count is `villains.length + 1` (hand/init.ts), NOT this. Widened from {6,9} at TB0 §9 F1 so any
     *  2..9 table is expressible - 9-handed full ring is the cash default, 6/7 equally legal. */
    tableSize: z.number().int().min(2).max(9),
    stakes: StakesSchema,
    heroSeat: z.number().int().nonnegative(),
    /** pin, or let the generator choose. */
    heroPosition: PositionSchema.optional(),
    stackDepthBB: StackDepthBBSchema,
    villains: z.array(z.object({
        seat: z.number().int().nonnegative(),
        archetypeId: ArchetypeIdSchema,
        /** three-tag blend for the read system; weights sum to 1. */
        blend: z
            .array(z.object({ archetypeId: ArchetypeIdSchema, weight: z.number().min(0).max(1) }))
            .optional(),
    })),
    /** The tournament game-condition block (ST2). Present iff `gameType === 'tournament'`; a cash spot
     *  omits it entirely, which is what keeps the tournament layer additive - `strategy/match.ts` gates
     *  the stage-scoped exploit rules on a stage being present, so a cash spot can never fire one.
     *
     *  `phase` IS ST2's "tourneyStage" - TournamentStageSchema is a declared alias of
     *  TournamentPhaseSchema (above), so a second field would be the same enum under a second name.
     *  The remaining ST2 fields are optional/additive: a caller that only knows the stage + ladder (the
     *  pre-ST2 shape) still parses unchanged. */
    tournament: z
        .object({
        phase: TournamentPhaseSchema,
        payouts: z.array(z.number()),
        stacks: z.array(z.number()),
        heroStackRank: z.number().int().optional(),
        /** 1-based level in the blind schedule this hand is dealt at (tournament/blinds.ts). */
        blindLevel: z.number().int().positive().optional(),
        /** The BIG-BLIND ante for the level, in chips - the whole table's ante, posted once per hand,
         *  NOT a per-player ante. 0/omitted = no ante. See scenario/tourney.ts `perPlayerAnte` for how
         *  this maps onto the engine's per-player `stakes.ante`. */
        ante: z.number().nonnegative().optional(),
        /** Hero's effective stack in BB at this level - the number tournament strategy keys on (staging,
         *  push/fold band, ICM depth). Derived, but carried so a spec is self-describing. */
        effectiveStackBB: z.number().positive().optional(),
        /** The DT payout-structure preset id this spot's ladder came from, e.g. 'local_100'. An id, not
         *  the structure: specs stay serializable + data stays injected (the engine never loads JSON). */
        payStructureId: z.string().optional(),
        /** The DT/ST1 blind-schedule id, e.g. 'local_100'. */
        scheduleId: z.string().optional(),
        /** The ICM pressure this spot is played under (ST-LIB). Engine-derived from the DT structure's
         *  stage snapshot (`strategy/icm.ts` riskProfile → icmPressureBucket), carried so a postflop
         *  tournament hand's card-free digest can label its condition WITHOUT re-running ICM at deal time -
         *  the library generator stamps it, and liveRunner threads it into `FactsDigest.context.icmPressure`
         *  so the coach prompt (`ICM ${pressure}`) and the coachCacheKey both see the tournament condition.
         *  Absent ⇒ chip-EV (deep/early play), exactly like a cash spot. */
        icmPressure: IcmPressureSchema.optional(),
    })
        .optional(),
    /** lesson targeting: weight spot families, e.g. 'srp_ip_cbet', 'river_bluffcatch'. */
    spotWeights: z.record(z.string(), z.number()).optional(),
    // D3 read-axis shell fields (North Star §5) - optional/additive so the Local/Pro split and the
    // Observe/Guided/Solo spine stay CONFIG, not new engines. The generator uses `tableType` to pick a
    // table-type preset (data/table-types/table-types.v1.json) as Axis B's scripted ground truth.
    /** build a table of a KNOWN type; omit to let the generator choose / stay unlabeled. */
    tableType: TableTypeSchema.optional(),
    /** which layer of the Local spine this scenario runs in. */
    learningLayer: LearningLayerSchema.optional(),
    /** Local (v1) vs Pro (later phase, architected now). */
    trainingContext: TrainingContextSchema.optional(),
    seed: z.string(),
});
//# sourceMappingURL=scenario.js.map